import { HttpEvent, HttpEventType } from '@angular/common/http';

/** Viewer file-size restriction: video/3D-model files above this size are not opened in-app. */
export const MAX_VIEWER_FILE_SIZE_BYTES = 60 * 1024 * 1024; // 60 MB

/** Extensions the 60MB viewer restriction applies to (per business requirement). */
const SIZE_RESTRICTED_EXTENSIONS = ['mp4', 'fbx', 'glb'];

export function isSizeRestrictedExtension(extension: string | null | undefined): boolean {
  return !!extension && SIZE_RESTRICTED_EXTENSIONS.includes(extension.toLowerCase());
}

export function formatMb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function sizeExceedsLimit(extension: string | null | undefined, sizeBytes: number | null | undefined): boolean {
  return isSizeRestrictedExtension(extension) && !!sizeBytes && sizeBytes > MAX_VIEWER_FILE_SIZE_BYTES;
}

export function sizeExceededMessage(extension: string, totalBytes: number): string {
  return (
    `This .${extension.toLowerCase()} file is ${formatMb(totalBytes)}, which exceeds the ` +
    `${formatMb(MAX_VIEWER_FILE_SIZE_BYTES)} viewer size restriction. ` +
    `Please download the file to view it instead.`
  );
}

/**
 * Extracts the best-known total byte size from an in-flight HTTP event (works with
 * `observe: 'events', reportProgress: true` requests), so a request can be aborted as soon as
 * the server-reported Content-Length is known — before the full body is downloaded.
 */
export function getEventTotalBytes(event: HttpEvent<unknown>): number | null {
  if (event.type === HttpEventType.DownloadProgress) {
    return event.total ?? null;
  }
  if (event.type === HttpEventType.ResponseHeader || event.type === HttpEventType.Response) {
    const contentLength = event.headers?.get('content-length');
    return contentLength ? Number(contentLength) : null;
  }
  return null;
}
